import { useState, useRef, useEffect } from "react";
import { Form, Upload, Button, message, Select, Card, Row, Col, Table, List, Typography, Space, InputNumber, Input, Popconfirm } from "antd";
import type { UploadProps } from "antd";
import { UploadOutlined, FileExcelOutlined, EditOutlined, DeleteOutlined, DownloadOutlined } from "@ant-design/icons";
import { post } from "@/common/services/httpClient";
import { API_ENDPOINTS, QUERY_KEYS } from "../../api/apiEndpoints";
import { useAppMutation } from "@/common/hooks/useAppMutation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { UploadFile, RcFile } from "antd/es/upload/interface";
import type { ColumnsType } from "antd/es/table";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import type { ServiceItemPricingResponse, ServiceItemPricingType } from "../../types/providersTypes";
import { useNavigate } from "@tanstack/react-router";
import Export from "@/common/components/Export";
import { useTranslation } from 'react-i18next';
import type { CategoryType } from "../../../medicalServices/types/medicalServicesTypes";


const { Text } = Typography;
type CellValue = string | number | boolean | null;


// Function to format snake_case or camelCase to Title Case
const toTitleCase = (str: string) => {
    const result = str.replace(/([A-Z])/g, " $1");
    return result.charAt(0).toUpperCase() + result.slice(1);
};

interface ApiResponseError {
    isError: true;
    message: string;
}

interface InvalidRecord {
    [key: string]: CellValue | { [key: string]: string };
    id: string | number;
    serviceCategoryName: string;                       // Column 1
    medicalProviderServiceCategoryName: string;        // Column 2
    serviceCategoryItemName: string;                   // Column 3
    serviceItemCode: string;                           // Column 4
    medicalProviderServiceCategoryItemName: string;    // Column 5
    amount: number | string;                           // Column 6
    validationErrors: { [key: string]: string };       // validation messages
}

interface UploadSummary {
    totalRecords: number;
    savedRecords: number;
    failedRecords: number;
}

interface BulkUploadResponse {
    message: string;
    totalRecords: number;
    savedRecords: number;
    failedRecords: number;
    invalidRecords?: InvalidRecord[];
}

export interface ActionResponse<T = any> {
    data?: T | null;
    paging?: any;
    statusCode?: number;
    isError: boolean;
    title?: string;
    message?: string;
    developerMessage?: string;
    messageArgs?: string[] | null;
    id?: number;
    userType?: string | null;
}


// ---------------- Component ----------------
export default function MedicalProviderPricingPage() {

    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [file, setFile] = useState<File | null>(null);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [invalidRecords, setInvalidRecords] = useState<InvalidRecord[]>([]);
    const [uploadSummary, setUploadSummary] = useState<UploadSummary | null>(null);
    const [medicalProviderId, setMedicalProviderId] = useState<number | null>(null);
    const [serviceItemPricing, setServiceItemPricing] = useState<ServiceItemPricingType[]>([]);
    const [editingRowId, setEditingRowId] = useState<number | null>(null);
    const [loadingId, setLoadingId] = useState<number | null>(null); // spinner for save/delete
    const [backupRow, setBackupRow] = useState<ServiceItemPricingType | null>(null);
    const [selectedProviderName, setSelectedProviderName] = useState<string>("");
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchBy, setSearchBy] = useState<string>("Main Service");
    const [searchText, setSearchText] = useState<string>("");
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);



    const { data: categoriesList } = useQuery({
        queryKey: ["tpa_categories_with_items"],
        queryFn: async () => {
            const response = await post<{ data: CategoryType[] }>(
                API_ENDPOINTS.PROVIDERS.GET_TPA_CATEGORY_ITEMS,
                { pageNumber: 0, pageSize: 0, searchTerm: "" } // pageSize must be > 0
            );
            return response.data;
        },
    });


    const handleDownloadTemplate = async () => {
        try {
            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet("Template");

            // ---- COLUMN HEADERS ----
            sheet.columns = [
                //{ header: "TPA Service Category", key: "tpaCategory", width: 30 },
                { header: "Main Service", key: "tpaCategory", width: 30 },
                //{ header: "Medical Provider Service Category", key: "providerCategory", width: 35 },

                { header: "Sub Service", key: "tpaCategoryItem", width: 30 },
                //{ header: "TPA Service Category Item", key: "tpaCategoryItem", width: 30 },
                //{ header: "Medical Provider Service Category Item", key: "providerCategoryItem", width: 35 },

                { header: "Amount", key: "amount", width: 30 }
            ];

            // ---- POPULATE FIRST COLUMN WITH CATEGORIES ----
            if (categoriesList && categoriesList.length > 0) {
                categoriesList.forEach((cat: any) => {

                    if (cat.items && cat.items.length > 0) {
                        cat.items.forEach((item: any) => {
                            sheet.addRow({
                                tpaCategory: cat.name,
                                //providerCategory: "",
                                tpaCategoryItem: item.name,
                                //providerCategoryItem: "",
                                amount: ""
                            });
                        });
                    } else {
                        // fallback if no items
                        sheet.addRow({
                            tpaCategory: cat.name,
                            //providerCategory: "",
                            tpaCategoryItem: "",
                            //providerCategoryItem: "",
                            amount: ""
                        });
                    }

                });
            }

            // ---- STYLE HEADER ----
            sheet.getRow(1).eachCell((cell) => {
                cell.font = { bold: true };
                cell.alignment = { vertical: "middle", horizontal: "center" };
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFB0B0B0" }
                    //fgColor: { argb: "FFE8E8E8" }
                };
            });

            // ---- GENERATE FILE ----
            const buffer = await workbook.xlsx.writeBuffer();
            saveAs(new Blob([buffer]), "MedicalProviderPricingTemplate.xlsx");

        } catch (error) {
            console.error("Excel generation error:", error);
            message.error("Failed to generate template.");
        }
    };



    // useEffect
    useEffect(() => {
        if (uploadSummary && uploadSummary.savedRecords > 0) {
            refreshExistingProcess();
        }
    }, [uploadSummary]);

    const refreshExistingProcess = () => {
        if (!medicalProviderId) return;
        queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.PROVIDERS.MEDICALPROVIDER_SERVICE_ITEM_PRICING, medicalProviderId],
        });
    };


    const resultsRef = useRef<HTMLDivElement>(null);

    // ---------------- Fetch Providers ----------------
    const { data: providerOptions, isLoading: loadingProviders } = useQuery({
        queryKey: [QUERY_KEYS.PROVIDERS.MEDICALPROVIDERS],
        queryFn: async () => {
            try {
                const response = await post<{ data: { name: string; id: number }[] }>(
                    API_ENDPOINTS.PROVIDERS.SEARCH_MEDICALPROVIDER,
                    {
                        StatusId: 1,
                        IsInNetwork: true,
                        PageNumber: 0,
                        PageSize: 0,


                    }
                );
                return response.data.map((item) => ({ label: item.name, value: item.id }));
            } catch (error) {
                console.error(t("Failed to load medical providers.:"), error);
                message.error(t("Failed to load medical providers."));
                return [];
            }
        },
    });

    // ---------------Fetch medical providers  services item price
    const { data: fetchedPricing, isLoading: loadingPricing, refetch } = useQuery<ServiceItemPricingResponse>({
        queryKey: [
            QUERY_KEYS.PROVIDERS.MEDICALPROVIDER_SERVICE_ITEM_PRICING,
            medicalProviderId,
            pageNumber,
            pageSize,
            searchBy,
            searchText,
        ],
        queryFn: async (): Promise<ServiceItemPricingResponse> => {
            if (!medicalProviderId) {
                return {
                    data: [],
                    paging: { totalCount: 0, pageNumber: 0, pageSize: 0 },
                    statusCode: 200,
                    isError: false,
                    title: "",
                    developerMessage: "",
                    message: "",
                    messageArgs: null,
                    userType: null
                };
            }
            const response = await post<ServiceItemPricingResponse>(
                API_ENDPOINTS.PROVIDERS.GET_MEDICALPROVIDER_SERVICESITEMS_PRICING,
                {
                    medicalProviderId,
                    pageNumber,
                    pageSize,
                    searchBy: searchBy?.trim() ?? "",  // e.g. "code", "serviceCategory", "serviceCategoryItem"
                    searchText: searchText?.trim() ?? "",  // user entered text}S
                }
            );

            return response;
        },
        enabled: !!medicalProviderId,  // only run when medicalProviderId is set
    });

    //-------------------- Set provider pricing--------------------

    useEffect(() => {
        if (fetchedPricing) {
            setServiceItemPricing(fetchedPricing.data);
        }
    }, [fetchedPricing]);


    // ---------------- Upload Pricing Mutation ----------------
    const uploadMutation = useAppMutation(
        (formData: FormData) =>
            post<BulkUploadResponse>(
                API_ENDPOINTS.PROVIDERS.SAVE_BULK_MEDICALPROVIDER_SERVICE_ITEM_PRICING, // <-- new API
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            ),
        {
            errorMessagePrefix: "Upload failed",
            invalidateQueryKey: QUERY_KEYS.PROVIDERS.MEDICALPROVIDER_SERVICE_ITEM_PRICING, // <-- new query key
            onSuccess: (response) => {

                // Empty file warning (check message or totalRecords)
                if (
                    response?.message?.toLowerCase().includes("empty") ||
                    response?.totalRecords === 0
                ) {
                    message.warning(t(response?.message ?? "File is empty."));
                    return;
                }

                setUploadSummary({
                    totalRecords: response.totalRecords,
                    savedRecords: response.savedRecords,
                    failedRecords: response.failedRecords,
                });

                if (response.invalidRecords?.length) {
                    message.warning(t(response.message) || t("Some records have validation errors."));
                    setInvalidRecords(response.invalidRecords);
                } else {
                    message.success(t(response.message) || t("Pricing uploaded successfully."));
                    setInvalidRecords([]);
                    form.resetFields();
                    setFile(null);
                    setFileList([]);
                }
            },
        }
    );

    // ---------------- Form Submit ----------------
    const onFinish = async () => {
        if (!file) {
            message.error(t("Please upload an Excel file."));
            return;
        }
        if (!medicalProviderId) {
            message.error(t("Please select a medical provider."));
            return;
        }

        setInvalidRecords([]);
        setUploadSummary(null);


        const formData = new FormData();
        formData.append("File", file, file.name);
        formData.append("MedicalProviderId", String(medicalProviderId));

        uploadMutation.mutate(formData);
    };

    // ---------------- Upload Config ----------------
    const uploadProps: UploadProps = {
        accept: ".xlsx,.xls",
        fileList,
        beforeUpload: (uploadedFile: RcFile) => {
            setFile(uploadedFile);
            setFileList([uploadedFile]);
            setInvalidRecords([]);
            setUploadSummary(null);
            return false;
        },
        onRemove: () => {
            setFile(null);
            setFileList([]);
            setInvalidRecords([]);
            setUploadSummary(null);
            return true;
        },
        maxCount: 1,
    };

    const handleFieldChange = (id: number, field: keyof ServiceItemPricingType, value: any) => {
        setServiceItemPricing((prev) =>
            prev.map((item) =>
                item.id === id ? { ...item, [field]: value } : item
            )
        );
    };

    // --- Save API Call ---
    const handleSave = async (record: ServiceItemPricingType) => {
        try {

            // --- Validation Start ---
           /* const validationfailed = false;*/

            //Frontend validation before sending API
            //if (!record.serviceItemCode?.trim()) {
            //    message.error(t("Medical Provider Service Item Code is required."));

            //    validationFailed = true;
            //}

            //if (!record.medicalProviderServiceCategoryName?.trim()) {
            //    message.error(t("Medical Provider Service Category is required."));

            //    validationFailed = true;
            //}

            //if (!record.medicalProviderServiceCategoryItemName?.trim()) {
            //    message.error(t("Medical Provider Service Category Item is required."));
            //    validationFailed = true;
            //}

            // --- If validation failed, revert and stop ---
            //if (validationFailed) {
            //    if (backupRow && backupRow.id === record.id) {
            //        setServiceItemPricing(prev =>
            //            prev.map(item => (item.id === backupRow.id ? backupRow : item))
            //        );
            //    }
            //    return;
            //}

            setLoadingId(record.id);

            const response = await post<ActionResponse>(API_ENDPOINTS.PROVIDERS.UPDATE_MEDICALPROVIDER_SERVICE_ITEM, {
                id: record.id,
                serviceItemCode: record.serviceItemCode,
                amount: record.amount,
                serviceCategoryId: record.serviceCategoryId,
                serviceCategoryItemId: record.serviceCategoryItemId
                //medicalProviderServiceCategoryName: record.medicalProviderServiceCategoryName,
                //medicalProviderServiceCategoryItemName: record.medicalProviderServiceCategoryItemName
            });

            if (response?.isError) {
                message.error(t(response?.message ?? "Validation error occurred."));

                // Revert state to backupRow if save fails
                if (backupRow && backupRow.id === record.id) {
                    setServiceItemPricing(prev =>
                        prev.map(item => (item.id === backupRow.id ? backupRow : item))
                    );
                }

                return; // stop further processing
            } else {
                message.success(t(response?.message ?? "Medical Provider Service Item updated successfully!"));
                setEditingRowId(null);
            }

        } catch (error: any) {
            console.error(t("Failed to update pricing:"), error);
            message.error(t(error?.response?.data?.message) || t("Something went wrong while updating!"));

            //  Revert to backup in case of exception
            if (backupRow && backupRow.id === record.id) {
                setServiceItemPricing(prev =>
                    prev.map(item => (item.id === backupRow.id ? backupRow : item))
                );
            }

        } finally {
            setLoadingId(null);
        }
    };

    const handleEdit = (record: ServiceItemPricingType) => {
        // If another row is being edited and not saved yet, revert it
        if (editingRowId && editingRowId !== record.id && backupRow) {
            setServiceItemPricing(prev =>
                prev.map(item => (item.id === backupRow.id ? backupRow : item))
            );
        }

        // Now start editing the new row
        setBackupRow({ ...record }); // save current state as backup
        setEditingRowId(record.id);
    };


    // --- Delete API Call ---
    const handleDelete = async (id: number) => {
        try {
            setLoadingId(id);

            const response = await post<ApiResponseError>(API_ENDPOINTS.PROVIDERS.DELETE_MEDICALPROVIDER_SERVICE_ITEM, id);
            //console.log('deleted', response);
            if (response.isError) {
                message.error(response.message)
            } else {
                message.success(t("Record deleted!"));
                setServiceItemPricing((prev) => prev.filter((item) => item.id !== id));
            }

        } catch (error) {
            console.error(t("Failed to delete record:"), error);
            message.error(t("Failed to delete record!"));
        } finally {
            setLoadingId(null);
        }
    };


    type ValidationKeyMap = {
        serviceCategoryName: string;
        serviceCategoryItemName: string;
        amount: string;
    };

    const validationKeyMap: ValidationKeyMap = {
        serviceCategoryName: "Service Category",
        serviceCategoryItemName: "Sub Service",
        amount: "Amount",
    };

    // ----------------- COLUMN PRICING LISTING----------------

    const generateColumns = (records: InvalidRecord[]): ColumnsType<InvalidRecord> => {
        if (records.length === 0) return [];



        const excludedKeys = new Set(['validationErrors', 'errorInBulk', 'isActive', 'id', 'medicalProviderName',
            'medicalProviderId', 'serviceCategoryId', 'serviceCategoryItemId', 'serviceCategoryNameAr',
            'serviceCategoryItemNameAr', "serviceItemCode", "medicalProviderServiceCategoryName", "medicalProviderServiceCategoryItemName"]);

        const primaryKey = 'serviceCategoryName';

        const firstRecord = records[0];
        const keys = Object.keys(firstRecord);

        const dynamicKeys = keys.filter(key => key !== primaryKey && !excludedKeys.has(key));

        const renderCell = (text: CellValue, record: InvalidRecord, key: string) => {
            const hasError = Object.keys(record.validationErrors)
                .some(errKey => errKey.toLowerCase() === key.toLowerCase());
            const isEmpty = text === null || text === undefined || text === "";

            const showHighlight = hasError || isEmpty;


            const cellStyle: React.CSSProperties = {
                backgroundColor: showHighlight ? "#fff1f0" : "transparent",
                color: hasError ? "#cf1322" : "inherit",
                margin: "-16px",
                padding: "16px",
                height: "100%",              // full height
                minHeight: "100%",           // cover wrapped lines
                display: "flex",
                alignItems: "center",
                boxSizing: "border-box",
            };


            //  put invisible character for empty cells 
            const displayText = isEmpty ? "\u00A0" : text; // non-breaking space

            return <div style={cellStyle}>{displayText}</div>;
        };

        const columnHeaderMap: Record<string, string> = {
            serviceCategoryName: "Main Service",
            //medicalProviderServiceCategoryName: "Medical Provider Service Category",
            serviceCategoryItemName: "Sub Service",
            //serviceItemCode: "Medical Provider Service Item Code",
            //medicalProviderServiceCategoryItemName: "Medical Provider Service Category Item",
            amount: "Amount"
        };

        const primaryColumn: ColumnsType<InvalidRecord>[0] = {
            title: columnHeaderMap[primaryKey] || toTitleCase(primaryKey),
            dataIndex: primaryKey,
            key: primaryKey,
            fixed: 'left',
            width: 320,
            render: (text: CellValue, record: InvalidRecord) =>
                renderCell(text, record, primaryKey)
        };

        const dynamicColumns: ColumnsType<InvalidRecord> = dynamicKeys.map(key => ({
            title: columnHeaderMap[key] || toTitleCase(key),
            dataIndex: key,
            key: key,
            render: (text: CellValue, record: InvalidRecord) =>
                renderCell(text, record, key)
        }));

        return [primaryColumn, ...dynamicColumns];
    };

    const tableColumns = generateColumns(invalidRecords);



    const pricingColumns: ColumnsType<ServiceItemPricingType> = [
        {
            title: t("Main Service"),
            dataIndex: "serviceCategoryName",
            render: (text) =>
            (
                text
            ),
        },
        //{
        //    title: t("Medical Provider Service Category"),
        //    dataIndex: "medicalProviderServiceCategoryName",
        //    render: (text, record) =>
        //        editingRowId === record.id ? (
        //            <Input
        //                value={record.medicalProviderServiceCategoryName}
        //                onChange={(e) =>
        //                    handleFieldChange(record.id, "medicalProviderServiceCategoryName", e.target.value)
        //                }
        //            />
        //        ) : (
        //            text
        //        ),
        //},
        //{
        //    title: t("Medical Provider Service Item Code "), dataIndex: "serviceItemCode",
        //    render: (text, record) =>
        //        editingRowId === record.id ? (
        //            <Input
        //                value={record.serviceItemCode}
        //                onChange={(e) => handleFieldChange(record.id, "serviceItemCode", e.target.value)}
        //                onPressEnter={() => handleSave(record)}
        //            //onBlur={() => handleSave(record)}
        //            />
        //        ) : (
        //            text
        //        ),
        //},
        {
            title: t("Sub Service"),
            dataIndex: "serviceCategoryItemName",
            render: (text) =>
            (
                text
            )
        },
        //{
        //    title: t("Medical Provider Service Category Item"),
        //    dataIndex: "medicalProviderServiceCategoryItemName",
        //    render: (text, record) =>
        //        editingRowId === record.id ? (
        //            <Input
        //                value={record.medicalProviderServiceCategoryItemName}
        //                onChange={(e) =>
        //                    handleFieldChange(record.id, "medicalProviderServiceCategoryItemName", e.target.value)
        //                }
        //            />
        //        ) : (
        //            text
        //        ),
        //},
        {
            title: t("Amount"), dataIndex: "amount",
            render: (text, record) => {
                if (editingRowId === record.id) {
                    return (
                        <InputNumber
                            min={0}
                            value={record.amount}
                            onChange={(value) => handleFieldChange(record.id, "amount", value ?? 0)}
                            onPressEnter={() => handleSave(record)} //on Enter save
                        //onBlur={() => handleSave(record)} // Blur save
                        />
                    );
                }
                return text;
            },
        },
        {
            title: t("Actions"),
            key: "actions",
            render: (_, record) => (
                <Space>
                    {editingRowId === record.id ? (
                        <>
                            <Button
                                type="primary"
                                loading={loadingId === record.id}
                                onClick={() => handleSave(record)}
                            >
                                {t("Save")}
                            </Button>

                            <Button
                                type="default"
                                onClick={() => {
                                    if (backupRow) {
                                        setServiceItemPricing(prev =>
                                            prev.map(item => (item.id === backupRow.id ? backupRow : item))
                                        );
                                    }
                                    setEditingRowId(null);
                                    setBackupRow(null);
                                }}
                            >
                                {t("Cancel")}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                icon={<EditOutlined />}
                                type="default"
                                onClick={() => {
                                    handleEdit(record)
                                }}
                            >
                                {/* t("Edit") */}
                            </Button>
                            <Popconfirm
                                title={t("Are you sure to delete this record?")}
                                okText={t("Yes")}
                                cancelText={t("No")}
                                onConfirm={() => handleDelete(record.id)}
                            >
                                <Button
                                    icon={<DeleteOutlined />}
                                    type="default"
                                    danger
                                    loading={loadingId === record.id}
                                >
                                    {/* t("Delete") */}
                                </Button>
                            </Popconfirm>
                        </>
                    )
                    }
                </Space >
            ),
        }
    ];

    // ---------------- Export Errors ----------------

    const expandedRowRender = (record: InvalidRecord) => {
        const errors = Object.entries(record.validationErrors) as [keyof ValidationKeyMap, string][];
        return (
            <List
                header={<Text strong>Validation Errors</Text>}
                dataSource={errors}
                renderItem={([field, error]) => (
                    <List.Item>
                        <Text strong> {validationKeyMap[field] || toTitleCase(field)}:</Text> <Text type="danger">{error}</Text>
                    </List.Item>
                )}
                bordered
                size="small"
            />
        );
    };

    const exportInvalidRecords = async () => {
        if (!invalidRecords.length) {
            message.warning(t("No invalid records to export."));
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(t("Invalid Pricing"));

        worksheet.columns = [
            { header: t("Main Service"), key: "serviceCategoryName", width: 30 },
            //{ header: t("Medical Provider Service Category"), key: "medicalProviderServiceCategoryName", width: 30 },
            { header: t("Sub Service"), key: "serviceCategoryItemName", width: 30 },
            //{ header: t("Medical Provider Service Item Code "), key: "serviceItemCode", width: 30 },
            //{ header: t("Medical Provider Service Category Item"), key: "medicalProviderServiceCategoryItemName", width: 40 },
            { header: t("Amount"), key: "amount", width: 30 },
        ];

        // Format header row
        const headerRow = worksheet.getRow(1);
        headerRow.font = { bold: true, color: { argb: "FF000000" } }; // black bold text
        headerRow.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFD9D9D9" }, // light grey background
        };
        headerRow.alignment = { vertical: "middle", horizontal: "center" };
        headerRow.height = 20;

        //  Add invalid records and highlight error cells
        invalidRecords.forEach((rec) => {

            // sanitize values (remove invisible spaces)
            const cleanRecord = Object.fromEntries(
                Object.entries(rec).map(([k, v]) => [k, typeof v === "string" ? v.replace(/\u00A0/g, "").trim() : v])
            );

            const row = worksheet.addRow(cleanRecord);

            worksheet.columns.forEach((col, idx) => {
                const key = col.key as string;
                const cell = row.getCell(idx + 1);
                const value = cleanRecord[key];

                const isEmpty = value === null || value === undefined || value === "";
                const hasError = rec.validationErrors && Object.keys(rec.validationErrors)
                    .some(errKey => errKey.toLowerCase() === key.toLowerCase());

                // Highlight if empty or has validation error
                if (isEmpty || hasError) {
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: isEmpty ? "FFFFF2F2" : "FFFFE5E5" } // lighter red for empty, darker for invalid
                    };
                    cell.font = { color: { argb: "FFFF0000" } }; // red text
                }
            });

        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), t("Invalid_Pricing.xlsx"));
    };

    const placeholderMap: Record<string, string> = {
        TPAServiceCategory: t("Search by Main Service"),
        //MedicalProviderServiceCategory: t("Search by Medical Provider Service Category"),
        TPAServiceCategoryItem: t("Search by Sub Service"),
        //MedicalProviderServiceCategoryItem: t("Search by Medical Provider Service Category Item"),
    };


    // ---------------- Render ----------------
    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <Button htmlType="button" onClick={() => navigate({ to: '..' })}>
                    {t("Go Back")}
                </Button>
            </div>

            <Card
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{t("Medical Provider - Service Category Item Pricing")}</span>
                        {/*<a href="/bulk_medicalProviderServiceItemPricing_template.xlsx" download>*/}
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={handleDownloadTemplate}
                            style={{ backgroundColor: '#e6f7ff', borderColor: '#91d5ff', color: '#096dd9' }}
                        >
                            {t("Download Template")}
                        </Button>
                        {/*</a>*/}
                    </div>
                }
            >
                <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                label={t("Medical Provider")}
                                name="medicalProviderId"
                                rules={[{ required: true }]}
                            >
                                <Select
                                    size="large"
                                    placeholder={t("Select medical provider")}
                                    options={providerOptions}
                                    loading={loadingProviders}
                                    onChange={(val, option) => {
                                        setMedicalProviderId(val);

                                        // reset Upload Summary and invalid records when provider changes
                                        setUploadSummary(null);
                                        setInvalidRecords([]);
                                        setFile(null);
                                        setFileList([]);

                                        // type narrowing
                                        if (!Array.isArray(option)) {
                                            setSelectedProviderName(option?.label ?? "");
                                        }
                                    }}
                                    showSearch
                                />
                            </Form.Item>
                        </Col>

                        {medicalProviderId && (
                            <Col span={4}>
                                <Form.Item
                                    label={t("Upload Pricing Template")}
                                    name="file"
                                    rules={[{ required: true, message: t("Please upload an excel file") }]}
                                >
                                    <Upload key={medicalProviderId} {...uploadProps} showUploadList>
                                        <Button icon={<UploadOutlined />}>{t("Select Excel File")}</Button>
                                    </Upload>
                                </Form.Item>
                            </Col>
                        )}

                        <Col span={4}>
                            <Form.Item style={{ textAlign: "left", marginTop: 30 }}>
                                <Button type="primary" htmlType="submit" loading={uploadMutation.isPending}>
                                    {t("Upload")}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>

            {medicalProviderId && (
                <Card
                    title={
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                            }}
                        >
                            <span>{t("existingPricing")} - {selectedProviderName}</span>

                            <Export
                                compact={true} // remove button margin
                                apiUrl={API_ENDPOINTS.PROVIDERS.GET_MEDICALPROVIDER_SERVICESITEMS_PRICING}
                                fileName="MedicalProviderPricing"
                                fileType="excel"
                                confirmButtonText={t("Do you want to export Existing Medical Provider Pricing?")}
                                columns={[
                                    { header: t("Main Service"), key: "serviceCategoryName" },
                                    //{ header: t("Medical Provider Service Category"), key: "medicalProviderServiceCategoryName" },
                                    //{ header: t("Medical Provider Service Item Code "), key: "serviceItemCode" },
                                    { header: t("Sub Service"), key: "serviceCategoryItemName" },
                                    //{ header: t("Medical Provider Service Category Item"), key: "medicalProviderServiceCategoryItemName" },
                                    { header: t("Amount"), key: "amount" },
                                ]}
                                defaultPayload={{
                                    IsExport: true,
                                    MedicalProviderId: medicalProviderId,
                                }}
                            />
                        </div>
                    }
                >
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col span={8}>
                            <Select
                                size="large"
                                value={searchBy}
                                onChange={(value) => setSearchBy(value)}
                                style={{ width: "100%" }}
                            >
                                <Select.Option value="TPAServiceCategory">{t("Main Service")}</Select.Option>
                                {/*<Select.Option value="MedicalProviderServiceCategory">{t("Medical Provider Service Category")}</Select.Option>*/}
                                {/*<Select.Option value="code">{t("Medical Provider Service Item Code ")}</Select.Option>*/}
                                <Select.Option value="TPAServiceCategoryItem">{t("Sub Service")}</Select.Option>
                                {/*<Select.Option value="MedicalProviderServiceCategoryItem">{t("Medical Provider Service Category Item")}</Select.Option>*/}
                            </Select>
                        </Col>

                        <Col span={8}>
                            <Input
                                placeholder={placeholderMap[searchBy] || t("Search")}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                allowClear
                            />
                        </Col>

                        <Col>
                            <Button type="primary" onClick={() => refetch()}>
                                {t("Search")}
                            </Button>
                        </Col>

                        <Col>
                            <Button onClick={() => { setSearchText(""); setSearchBy("Main Service"); refetch(); }}>
                                {t("Reset")}
                            </Button>
                        </Col>
                    </Row>

                    <Table
                        loading={loadingPricing}
                        dataSource={serviceItemPricing || []}
                        columns={pricingColumns}
                        rowKey="id"
                        bordered
                        pagination={{
                            current: pageNumber,
                            pageSize: pageSize,
                            total: fetchedPricing?.paging?.totalCount ?? 0,
                            onChange: (page, size) => {
                                setPageNumber(page);
                                setPageSize(size);
                            },
                        }}
                    />
                </Card>
            )}

            {uploadSummary && (
                <div ref={resultsRef}>
                    <Card title={`${t("Upload Results")} - ${selectedProviderName}`} style={{ marginTop: 16 }}>
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={6}>
                                <Text>
                                    <strong>{t("Total")}:</strong> {uploadSummary.totalRecords}
                                </Text>
                            </Col>
                            <Col span={6}>
                                <Text type="success">
                                    <strong>{t("Saved")}:</strong> {uploadSummary.savedRecords}
                                </Text>
                            </Col>
                            <Col span={6}>
                                <Text type="danger">
                                    <strong>{t("Failed")}:</strong> {uploadSummary.failedRecords}
                                </Text>
                            </Col>
                            <Col span={6} style={{ textAlign: "right" }}>
                                <Button icon={<FileExcelOutlined />} onClick={exportInvalidRecords}>
                                    {t("Export Errors")}
                                </Button>
                            </Col>
                        </Row>

                        {invalidRecords.length > 0 && (
                            <Table
                                dataSource={invalidRecords}
                                columns={tableColumns}
                                rowKey={(record) =>
                                    `row_${pageNumber}_${record.serviceCategoryName}_${record.serviceCategoryItemName}`
                                }
                                bordered
                                scroll={{ x: 'max-content' }}
                                //expandable={{ expandedRowRender }}
                                expandable={{
                                    expandedRowRender,
                                    expandedRowKeys,
                                    onExpand: (expanded, record) => {
                                        const key = `row_${pageNumber}_${record.serviceCategoryName}_${record.serviceCategoryItemName}`;
                                        setExpandedRowKeys((prev) =>
                                            expanded ? [...prev, key] : prev.filter((k) => k !== key)
                                        );

                                        //console.log("Row expand clicked:", record, "key:", key);
                                    },
                                }}
                            />
                        )}
                    </Card>
                </div>
            )}
        </>
    );

}
